import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { find, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { areSameValue } from '../../../core/utils'

import { EditableTableRowLoader } from '../../../common/components/sections'
import { StyledHighlightedTableRow } from '../../../common/components/styled'
import {
  EXCLUSION_STATUS,
  TABLE_ROW_HIGHLIGHT_STYLES,
} from '../../../common/constants'

import { useUpdateLocationMutation } from '../../hooks'

const LocationEditableRow = ({ ...rowProps }) => {
  const rowRef = useRef(null)
  const { 'data-row-key': id, children, className, ...restProps } = rowProps
  const [, { isLoading, error }] = useUpdateLocationMutation(id)

  const location = useSelector(store =>
    find(store.locations.locations?.data?.result, ({ id: locationId }) =>
      areSameValue(id, locationId),
    ),
  )

  const rowHighlight = useMemo(() => {
    if (location?.status === EXCLUSION_STATUS.EXCLUDED.value) {
      return [TABLE_ROW_HIGHLIGHT_STYLES.EXCLUDED]
    }

    if (location?.isRequiringAttention) {
      return [TABLE_ROW_HIGHLIGHT_STYLES.REQUIRES_ATTENTION]
    }

    return []
  }, [location])

  useEffect(() => {
    if (!isNil(error)) {
      showErrorNotification({
        title: 'Location update failed',
        description: error,
      })
    }
  }, [error])

  return (
    <>
      <EditableTableRowLoader
        isLoading={isLoading}
        height={rowRef?.current?.offsetHeight}
      />
      <StyledHighlightedTableRow
        data-row-key={id}
        ref={rowRef}
        highlight={rowHighlight}
        className={className}
        {...restProps}
      >
        {children}
      </StyledHighlightedTableRow>
    </>
  )
}

LocationEditableRow.propTypes = {
  rowProps: PropTypes.object,
}

LocationEditableRow.defaultProps = {
  rowProps: undefined,
}

export default LocationEditableRow
